import { ablakotNyit, dokumentumMegjelenites, esc } from './dokumentum-ablak.ts';

/**
 * Hordócímke nyomtatása Bluetooth-os hőnyomtatóra. (3.0 / 5.7)
 *
 * **A címkén csak a szállítólevél sorszáma van, nagy betűvel. Semmi más.**
 * A matrica a hordóra megy, hogy később vissza lehessen keresni, honnan jött.
 *
 * A nyomtatás a telefonon történik, nem a szerveren: a nyomtató a sofőr
 * kezében van. A böngésző Bluetooth felületét használjuk — ehhez Androidon
 * Chrome kell, és a rendszernek biztonságos (https) címen kell futnia. Ha a
 * böngésző nem alkalmas, megmondjuk, és az előnézet akkor is működik.
 */

export interface NyomtatoBeallitas {
  be: boolean;
  szeles: 56 | 58 | 80;
  pld: number;
  nev: string | null;
  /** Sorköz pontban; 0 = a nyomtató gyári sorköze. (2026-10-01) */
  sorkoz: number;
  /** Hány üres sort toljon ki a címke után. */
  tolas: number;
  /** A nyomtató nyelve: blokknyomtató vagy matricás címkenyomtató. (2026-10-07) */
  nyelv: NyomtatoNyelv;
  /** A matrica mérete milliméterben — csak a címkenyelvekhez kell. */
  cimke_szeles: number;
  cimke_magas: number;
}

export const NYOMTATO_NYELVEK = ['escpos', 'tspl', 'cpcl'] as const;
export type NyomtatoNyelv = (typeof NYOMTATO_NYELVEK)[number];

export const NYOMTATO_NYELV_NEVEK: Record<NyomtatoNyelv, string> = {
  escpos: 'ESC/POS — blokknyomtató',
  tspl: 'TSPL — matricás címkenyomtató',
  cpcl: 'CPCL — matricás címkenyomtató',
};

export const NYOMTATO_ALAP: NyomtatoBeallitas = {
  be: false,
  szeles: 56,
  pld: 1,
  nev: null,
  sorkoz: 0,
  tolas: 3,
  nyelv: 'escpos',
  cimke_szeles: 50,
  cimke_magas: 30,
};

/**
 * Ismert nyomtatószolgáltatások. (2026-10-07)
 *
 * Minden hőnyomtató más Bluetooth-szolgáltatáson fogadja a nyers
 * nyomtatóparancsokat. Korábban **csak az elsőt** ismertük, és csak olyan
 * eszközt kínáltunk fel, amelyik azt hirdeti magáról — ezért fordulhatott
 * elő, hogy a telefon látta a nyomtatót, a program viszont nem is mutatta.
 *
 * Mostantól minden közeli eszköz választható, és a kapcsolódás után
 * keressük meg, melyik szolgáltatáson lehet neki írni. Ez a lista csak a
 * gyors út: ha egyik sem passzol, végigjárjuk, amit a nyomtató kiad.
 */
const SZOLGALTATASOK: Array<{
  szolgaltatas: number | string;
  jellemzo?: number | string;
  nev: string;
}> = [
  { szolgaltatas: 0x18f0, jellemzo: 0x2af1, nev: 'ESC/POS (18F0)' },
  { szolgaltatas: 0xff00, jellemzo: 0xff02, nev: 'soros (FF00)' },
  { szolgaltatas: 0xffe0, jellemzo: 0xffe1, nev: 'soros (FFE0)' },
  { szolgaltatas: 0xfff0, jellemzo: 0xfff2, nev: 'soros (FFF0)' },
  {
    szolgaltatas: '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    jellemzo: '49535343-8841-43f4-a8d4-ecbe34729bb3',
    nev: 'átlátszó soros (ISSC)',
  },
  {
    szolgaltatas: '38eb4a80-c570-11e3-9507-0002a5d5c51b',
    jellemzo: '38eb4a82-c570-11e3-9507-0002a5d5c51b',
    nev: 'Zebra',
  },
  { szolgaltatas: '0000ae30-0000-1000-8000-00805f9b34fb', jellemzo: '0000ae01-0000-1000-8000-00805f9b34fb', nev: 'címkenyomtató (AE30)' },
  { szolgaltatas: 'e7810a71-73ae-499d-8c15-faa9aef0c3f2', jellemzo: 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f', nev: 'címkenyomtató (E781)' },
];

/**
 * Egy sor a címkén.
 *
 * `nagy` a sorszám; a `kiemelt` az eleje (MO vagy HSO), amit még nagyobban
 * szedünk, hogy a hordón egy pillantásra látszódjon, mi van benne.
 * `apro` a jogszabályi szöveg, `sor` a cégadat.
 */
export type CimkeSor = { tipus: 'nagy' | 'sor' | 'vonal' | 'apro'; sz: string; kiemelt?: string };

/**
 * A jogszabály szerinti megnevezés. Minden címkén rajta kell lennie.
 * Két sorba törve, mert egy 50 mm-es matricán nem fér ki egybe.
 */
export interface CegAdat {
  nev?: string;
  telefon?: string;
}

export const CIMKE_KATEGORIA = [
  '3. kat. állati eredetű melléktermék,',
  'emberi fogyasztásra nem alkalmas!',
];

/**
 * Rákerül-e a sorszámon kívül más is a címkére.
 *
 * A megrendelő először kérte a jogszabályi szöveget és a cégadatot, majd
 * ugyanaznap úgy döntött, hogy a címkén maradjon **csak a sorszám** — így a
 * típus sokkal nagyobb betűt kaphat, és kisebb matrica is elég. A szöveget
 * nem töröltük ki: ha mégis vissza kell, ezt az egy értéket kell igazra
 * állítani. (2026-10-07)
 */
export const CIMKE_SZOVEG = false;

/**
 * A címke tartalma. Alapesetben **csak a sorszám** — a megrendelő döntése
 * szerint (2026-10-07), hogy a típus nagy betűt kaphasson.
 *
 * A `szoveggel` bekapcsolásával a jogszabályi megnevezés és a cégadat is
 * rákerül. A cégnév és a telefonszám a **cégadatokból** jön, nem ide írva:
 * ha egyszer költözünk vagy számot váltunk, a címke magától követi.
 */
export function cimkeTartalom(
  szam: string,
  ceg?: CegAdat,
  szoveggel: boolean = CIMKE_SZOVEG,
): CimkeSor[] {
  const vago = szam.indexOf('/');
  const sorok: CimkeSor[] = [
    { tipus: 'nagy', sz: szam, kiemelt: vago > 0 ? szam.slice(0, vago) : undefined },
  ];
  if (!szoveggel) return sorok;
  sorok.push(...CIMKE_KATEGORIA.map((s) => ({ tipus: 'apro' as const, sz: s })));
  const cegSor = [ceg?.nev, ceg?.telefon].filter(Boolean).join(' ').trim();
  if (cegSor) sorok.push({ tipus: 'sor', sz: cegSor });
  return sorok;
}

/**
 * Raklapcímke: csak a fajta, a lehető legnagyobb betűvel.
 * (A megrendelő kérése, 2026-10-16.)
 *
 * A raktárban egy raklapra csak egyféle áru kerül, ezért a címkén nem is
 * kell más — a sorszámok az egyes hordókon úgyis rajta vannak. Ez a
 * matrica a fóliázott raklap oldalára megy, hogy messziről látszódjon,
 * HSO-e vagy moslék.
 */
export function raklapTartalom(tipus: 'HSO' | 'MO'): CimkeSor[] {
  return [{ tipus: 'nagy', sz: tipus, kiemelt: tipus }];
}

/**
 * A nyomtató nyelvére (ESC/POS) fordított címke.
 *
 * Ez a hőnyomtatók közös nyelve: néhány vezérlőbájt mondja meg, hogy jöjjön
 * középre igazítva, dupla méretben. Ékezetet nem küldünk — a kódlap
 * nyomtatónként más, és egy sorszámban úgysincs ékezet.
 */
export function escpos(
  sorok: CimkeSor[],
  szeles: number,
  /**
   * Sorköz és papírtolás — nyomtatónként más, ezért állítható.
   * (a megrendelő kérése, 2026-10-01)
   */
  extra?: { sorkoz?: number; tolas?: number },
): Uint8Array {
  const oszlop = szeles >= 80 ? 48 : 32;
  const sorkoz = Math.min(120, Math.max(0, Math.round(extra?.sorkoz ?? 0)));
  const tolas = Math.min(12, Math.max(0, Math.round(extra?.tolas ?? 3)));
  const b: number[] = [];
  /* Ékezet nélkül: a nyomtatók kódlapja más és más, így mindenhol olvasható. */
  const szoveg = (s: string) => {
    for (const jel of ekezetNelkul(s)) b.push(jel.charCodeAt(0));
  };

  b.push(27, 64); // alaphelyzet
  /*
   * Sorköz: 0 esetén a nyomtató gyári beállítása marad (ESC 2), különben
   * pontban adjuk meg (ESC 3 n). Van, amelyik készülék sűrűn fog, van,
   * amelyik ritkán — ezért kell állíthatónak lennie.
   */
  if (sorkoz > 0) b.push(27, 51, sorkoz);
  else b.push(27, 50);
  b.push(27, 97, 1); // középre

  for (const s of sorok) {
    if (s.tipus === 'nagy') {
      // Keskeny papíron a dupla széles betűből 16 fér ki; ha hosszabb, marad a sima.
      const dupla = s.sz.length <= Math.floor(oszlop / 2);
      /*
       * A sorszám eleje (MO vagy HSO) nagy betűvel és félkövéren megy, a
       * többi alatta, kisebben — így a hordón egy pillantásra látszik, mi
       * van benne. (2026-10-07, a megrendelő kérése)
       */
      if (s.kiemelt && s.sz.startsWith(s.kiemelt)) {
        /*
         * A típus **külön sorba**, nagy betűvel, alatta a sorszám többi
         * része. Egy soron belül a két méret egymáshoz igazítása
         * készülékenként máshogy sül el (hézag és elcsúszás), külön sorban
         * nincs mit eltalálni. (2026-10-07, a megrendelő jelzése)
         */
        const maradek = s.sz.slice(s.kiemelt.length);
        b.push(27, 69, 1); // félkövér
        b.push(29, 33, s.kiemelt.length * 3 <= oszlop ? 0x22 : 0x11);
        szoveg(s.kiemelt);
        b.push(10);
        b.push(29, 33, maradek.length * 2 <= oszlop ? 0x11 : 0x01);
        b.push(27, 69, 0);
        szoveg(maradek);
      } else {
        b.push(29, 33, dupla ? 0x11 : 0x01);
        szoveg(s.sz);
      }
      b.push(10);
      b.push(29, 33, 0);
    } else if (s.tipus === 'apro') {
      /* A jogszabályi szöveg a legkisebb betűvel — csak el kell férnie. */
      b.push(27, 77, 1); // kisebb betűkészlet
      szoveg(s.sz);
      b.push(10);
      b.push(27, 77, 0);
    } else if (s.tipus === 'vonal') {
      szoveg('-'.repeat(oszlop));
      b.push(10);
    } else {
      szoveg(s.sz);
      b.push(10);
    }
  }

  for (let i = 0; i < tolas; i += 1) b.push(10); // hogy legyen mit letépni
  return new Uint8Array(b);
}

/**
 * Ékezet nélküli alak. (2026-10-07)
 *
 * A hőnyomtatók kódlapja készülékenként más, és a jogszabályi szöveg így
 * kérdőjelekké esett szét („3. kat. ?llati eredet? mell?kterm?k"). Az
 * ékezet elhagyása minden nyomtatón ugyanazt a — olvasható — eredményt
 * adja, szemben a kódlap-találgatással.
 */
export function ekezetNelkul(szoveg: string): string {
  return (
    szoveg
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      /* A sortörés és a tabulátor marad: a címkenyelvek parancsait az
         újsor választja el egymástól. */
      .replace(/[^\x20-\x7e\r\n\t]/g, '')
  );
}

/** ASCII bájtok — a címkenyelvek sima szöveges parancsok. */
function ascii(szoveg: string): number[] {
  const b: number[] = [];
  for (const jel of ekezetNelkul(szoveg)) b.push(jel.charCodeAt(0));
  return b;
}

/**
 * A nyomtató beépített betűi: [szélesség, magasság] pontban. (2026-10-07)
 *
 * Ebből számoljuk a sorok helyét **és** azt, mekkora matrica kell a
 * tartalomhoz. A szöveget nem nyomjuk össze a beállított matricához — a
 * megrendelő inkább a szöveghez keres matricát.
 */
const TSPL_BETUK: Record<string, [number, number]> = {
  '1': [8, 12],
  '2': [12, 20],
  '3': [16, 24],
  '4': [24, 32],
  '5': [32, 48],
};

/**
 * A hely, amibe a címkének bele kell férnie, nyomtatópontban.
 * A matrica mérete mínusz a körbefutó 3-3 mm levegő.
 */
interface Keret {
  szeles: number;
  magas: number;
}

/**
 * Hány jel szélességével számolunk a típusnál.
 *
 * Mindig a leghosszabb típussal (HSO, ALT, MHJ — három jel), nem a
 * mostanival: különben az „MO" nagyobb betűt kapna, mint a „HSO", és a
 * kétféle címke nem hasonlítana egymásra. (2026-10-07)
 */
const mertekSzam = (kiemelt: string) => Math.max(kiemelt.length, 3);

/** Egy kinyomtatandó sor: melyik betűvel, mekkorán. */
interface CimkeElem {
  sz: string;
  betu: string;
  nagyitas: number;
}

/**
 * A típus mérete, növekvő sorrendben: [betű, nagyítás, szélesség jelenként].
 * Ebből választjuk a legnagyobbat, ami még kifér egy sorba. (2026-10-07)
 */
const TIPUS_MERETEK: Array<[string, number]> = [
  ['4', 2],
  ['5', 2],
  ['4', 3],
  ['5', 3],
  ['5', 4],
  ['5', 5],
];

/** Sorköz: ennyiszeres helyet foglal egy sor a betűmagasságához képest. */
const SORKOZ = 1.35;

/**
 * A címke sorai betűvel és nagyítással — ez a közös elrendezés.
 *
 * Az MO/HSO megjelölés akkora, amekkora még **elfér a sorszám sorában**:
 * a megrendelő kérése szerint addig nő, amíg a sorszám nem törne két
 * sorba. A mérce a címke leghosszabb másik sora (a jogszabályi szöveg) —
 * így a nagyobb típus nem kér nagyobb matricát. (2026-10-07)
 */
function elemek(sorok: CimkeSor[], keret?: Keret): CimkeElem[][] {
  const tobbiSzeles = Math.max(
    ...sorok
      .filter((s) => s.tipus !== 'nagy')
      .map((s) => s.sz.length * (TSPL_BETUK['2']?.[0] ?? 12)),
    0,
  );

  return sorok.flatMap((s): CimkeElem[][] => {
    if (s.tipus === 'nagy' && s.kiemelt && s.sz.startsWith(s.kiemelt)) {
      const maradek = s.sz.slice(s.kiemelt.length);
      /*
       * Ha a sorszám áll egyedül a címkén, **két sorba** tesszük: fölül a
       * típus nagy betűvel, alatta a sorszám többi része közepessel. Mindkettő
       * ugyanott, a bal margónál kezdődik.
       *
       * Azért külön sorban, mert egy soron belül a sorszám helyét a típus
       * *feltételezett* szélességéből kellene kiszámolni, a nyomtató pedig
       * készülékenként kicsit máshogy rajzolja a betűt — ebből lett az, hogy
       * a sorszám hézaggal jobbra és lejjebb került. Külön sorban nincs mit
       * eltalálni. (2026-10-07, a megrendelő jelzése)
       *
       * A típus akkora, amekkora még kifér a matricára szélességben és
       * magasságban is. Matricaméret nélkül (amikor azt számoljuk, mekkora
       * matrica kell) a legkisebb méretet adjuk, hogy a kiírt „legalább
       * ennyi kell" valóban a minimum legyen.
       */
      if (tobbiSzeles === 0) {
        const kozep: CimkeElem = { sz: maradek, betu: '3', nagyitas: 1 };
        const alsoMagas = elemMagas(kozep) * SORKOZ;
        let valasztott = TIPUS_MERETEK[0]!;
        for (const [betu, nagyitas] of TIPUS_MERETEK) {
          const jel = TSPL_BETUK[betu] ?? [24, 32];
          if (mertekSzam(s.kiemelt) * jel[0] * nagyitas > (keret?.szeles ?? 0)) continue;
          if (jel[1] * nagyitas * SORKOZ + alsoMagas > (keret?.magas ?? 0)) continue;
          valasztott = [betu, nagyitas];
        }
        return [
          [{ sz: s.kiemelt, betu: valasztott[0], nagyitas: valasztott[1] }],
          [kozep],
        ];
      }
      const maradekSzeles = maradek.length * (TSPL_BETUK['3']?.[0] ?? 16);
      const ferHely = Math.max(0, tobbiSzeles - maradekSzeles);
      const mertek = mertekSzam(s.kiemelt);
      /* A legnagyobb méret, ami még belefér; ha semmi, marad a legkisebb. */
      let valasztott = TIPUS_MERETEK[0]!;
      for (const [betu, nagyitas] of TIPUS_MERETEK) {
        const szeles = mertek * (TSPL_BETUK[betu]?.[0] ?? 24) * nagyitas;
        if (szeles <= ferHely) valasztott = [betu, nagyitas];
      }
      return [
        [
          { sz: s.kiemelt, betu: valasztott[0], nagyitas: valasztott[1] },
          { sz: maradek, betu: '3', nagyitas: 1 },
        ],
      ];
    }
    if (s.tipus === 'nagy') return [[{ sz: s.sz, betu: '3', nagyitas: 2 }]];
    /* A jogszabályi szöveg és a cégadat olvasható méretben — nem aprón. */
    return [[{ sz: s.sz, betu: '2', nagyitas: 1 }]];
  });
}

const PONT_MM = 8; // 203 dpi ≈ 8 pont/mm
const elemSzeles = (e: CimkeElem) => e.sz.length * (TSPL_BETUK[e.betu]?.[0] ?? 12) * e.nagyitas;
const elemMagas = (e: CimkeElem) => (TSPL_BETUK[e.betu]?.[1] ?? 20) * e.nagyitas;

/**
 * Mekkora matrica kell a tartalomhoz, milliméterben. (2026-10-07)
 *
 * A felületen kiírjuk, hogy ehhez tudjanak matricát venni — a szöveget
 * nem kicsinyítjük le a meglévőhöz.
 */
export const keretPont = (be: { cimke_szeles: number; cimke_magas: number }): Keret => ({
  szeles: Math.max(0, (Math.max(10, Math.round(be.cimke_szeles)) - 6) * PONT_MM),
  magas: Math.max(0, (Math.max(10, Math.round(be.cimke_magas)) - 6) * PONT_MM),
});

export function cimkeMeret(sorok: CimkeSor[]): { szeles: number; magas: number } {
  const sorokElemei = elemek(sorok);
  const szeles = Math.max(
    ...sorokElemei.map((e) => e.reduce((a, x) => a + elemSzeles(x), 0)),
    1,
  );
  const magas = sorokElemei.reduce(
    (a, e) => a + Math.max(...e.map(elemMagas)) * SORKOZ,
    0,
  );
  /* Két oldalt és alul-felül 3-3 mm levegő. */
  return {
    szeles: Math.ceil(szeles / PONT_MM) + 6,
    magas: Math.ceil(magas / PONT_MM) + 6,
  };
}

/**
 * Matricás címke TSPL nyelven. (2026-10-07)
 *
 * A címkenyomtató nem folyamatos szalagra ír, hanem **egy matricára**:
 * meg kell neki mondani a matrica méretét, a rést két matrica között, és
 * külön paranccsal, hogy most nyomtasson. Ettől lesz „egy címke egy lap".
 */
export function tspl(sorok: CimkeSor[], be: { cimke_szeles: number; cimke_magas: number }): Uint8Array {
  const sz = Math.max(10, Math.round(be.cimke_szeles));
  const m = Math.max(10, Math.round(be.cimke_magas));
  const p: string[] = [
    `SIZE ${sz} mm,${m} mm`,
    'GAP 2 mm,0 mm',
    'DIRECTION 1',
    'CLS',
  ];
  /*
   * A sorok egymás alatt, kényelmes sorközzel. A szöveget NEM nyomjuk
   * össze a beállított matricához: ha nem fér el, inkább nagyobb matrica
   * kell — a felület ki is írja, mekkora. (A megrendelő kérése.)
   */
  const bal = 3 * PONT_MM;
  const idezo = (t: string) => t.replace(/"/g, "'");
  let y = 3 * PONT_MM;
  for (const e of elemek(sorok, keretPont(be))) {
    const magas = Math.max(...e.map(elemMagas));
    let x = bal;
    for (const r of e) {
      /* A kisebb betűt a sor aljához igazítjuk, hogy egy vonalban álljon. */
      p.push(`TEXT ${x},${y + (magas - elemMagas(r))},"${r.betu}",0,${r.nagyitas},${r.nagyitas},"${idezo(r.sz)}"`);
      x += elemSzeles(r);
    }
    y += Math.round(magas * SORKOZ);
  }
  p.push('PRINT 1,1');
  return new Uint8Array(ascii(`${p.join('\r\n')}\r\n`));
}

/**
 * Matricás címke CPCL nyelven — a másik elterjedt címkenyelv.
 *
 * A fejlécben a felbontás, a magasság és a példányszám áll; a `FORM` és a
 * `PRINT` zárja a lapot.
 */
export function cpcl(sorok: CimkeSor[], be: { cimke_szeles: number; cimke_magas: number }): Uint8Array {
  const pont = (mm: number) => Math.round(mm * 8);
  const magas = pont(Math.max(10, Math.round(be.cimke_magas)));
  const p: string[] = [`! 0 200 200 ${magas} 1`];
  const bal = pont(3);
  /* Ugyanaz az elrendezés, mint TSPL-ben: kényelmes méret, nem összenyomva. */
  const CPCL_BETU: Record<string, string> = { '5': '7', '4': '5', '3': '4', '2': '1' };
  let y = pont(3);
  for (const e of elemek(sorok, keretPont(be))) {
    const sorMagas = Math.max(...e.map(elemMagas));
    let x = bal;
    for (const r of e) {
      /*
       * A nagyítás CPCL-ben a SETMAG parancs. Eddig csak félkövérre
       * állítottuk, a helyeket viszont a nagyított mérettel számoltuk —
       * ezért a típus nem lett nagyobb, a sorszám többi része pedig messze,
       * nagy üres hézag után kezdődött. (2026-10-07, javítás)
       */
      if (r.nagyitas > 1) p.push('SETBOLD 2', `SETMAG ${r.nagyitas} ${r.nagyitas}`);
      p.push(`TEXT ${CPCL_BETU[r.betu] ?? '1'} 0 ${x} ${y + (sorMagas - elemMagas(r))} ${r.sz}`);
      if (r.nagyitas > 1) p.push('SETMAG 1 1', 'SETBOLD 0');
      x += elemSzeles(r);
    }
    y += Math.round(sorMagas * SORKOZ);
  }
  p.push('FORM', 'PRINT');
  return new Uint8Array(ascii(`${p.join('\r\n')}\r\n`));
}

/** A címke a beállított nyomtatónyelven. */
export function cimkeAdat(sorok: CimkeSor[], be: NyomtatoBeallitas): Uint8Array {
  if (be.nyelv === 'tspl') return tspl(sorok, be);
  if (be.nyelv === 'cpcl') return cpcl(sorok, be);
  return escpos(sorok, be.szeles, { sorkoz: be.sorkoz, tolas: be.tolas });
}

/* --------------------------- a nyomtató maga --------------------------- */

interface Parositott {
  nev: string;
  /** Az eszköz maga — ebből tudunk újrakapcsolódni új párosítás nélkül. */
  eszkoz: BluetoothEszkoz;
  jellemzo: BluetoothJellemzo;
  /** Melyik szolgáltatáson megy a nyomtatás — hibakereséshez kiírjuk. */
  csatorna: string;
}

/** A párosított eszköz erre a munkamenetre. */
let nyomtato: Parositott | null = null;

export function parositottNev(): string | null {
  return nyomtato?.nev ?? null;
}

export function nyTamogatott(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth?.requestDevice;
}

export class NyomtatoHiba extends Error {}

const NEM_TUD =
  'Ez a böngésző nem tud Bluetooth-nyomtatóhoz kapcsolódni. Androidon Chrome kell hozzá, ' +
  'és a rendszernek biztonságos (https) címen kell futnia. Az előnézet így is működik.';

/**
 * Hőnyomtatók nevének szokásos kezdete. (2026-11-04)
 *
 * A megrendelő jelezte, hogy a párosításnál **túl sok eszközt** kínál fel a
 * program: ott van a listában minden közeli telefon, fülhallgató, óra és
 * autó is. Ez azért volt így, mert korábban a szűrés *túl szűk* volt (csak
 * azt mutatta, ami a nyomtatószolgáltatást hirdeti magáról), és a sofőr
 * nyomtatója emiatt meg sem jelent — ezért nyitottuk ki mindenre.
 *
 * Most középen vagyunk: alapból ezekre a nevekre és a nyomtatószolgáltatásokra
 * szűrünk, így rövid a lista. Ha a nyomtató mégsem lenne benne, egy külön
 * gombbal továbbra is előhozható **minden** eszköz — tehát a korábbi hiba nem
 * jöhet vissza, csak nem az az első út.
 */
export const NEV_KEZDETEK = [
  'Printer',
  'PRINTER',
  'printer',
  'Nyomtato',
  'Thermal',
  'THERMAL',
  'Label',
  'LABEL',
  'POS',
  'BTP',
  'BT-',
  'XP-',
  'XP_',
  'RPP',
  'MTP',
  'PT-',
  'GP-',
  'ZJ-',
  'HM-',
  'JK-',
  'EP-',
  'LPQ',
  'TD-',
  'QL-',
  'RJ-',
  'PJ-',
  'LK-',
  'Goojprt',
  'GOOJPRT',
  'Zebra',
  'ZQ',
  'ZD',
  'iMZ',
  'QLn',
  'Niimbot',
  'NIIMBOT',
  'Phomemo',
  'HPRT',
  'iDPRT',
  'Rongta',
  'SPRT',
  'TSC',
  'Bixolon',
  'Citizen',
  'Munbyn',
  'Brother',
  'Star',
  'D11',
  'B21',
  'M110',
  'M120',
  'M220',
];

/**
 * Amit a párosító ablakban felkínálunk: a nyomtatószolgáltatást hirdető
 * eszközök **és** a nyomtatónévvel kezdődők. Egy eszköznek elég az egyik
 * feltételnek megfelelnie, hogy megjelenjen.
 */
export function nyomtatoSzuro(): NonNullable<BluetoothKeres['filters']> {
  return [
    ...SZOLGALTATASOK.map((s) => ({ services: [s.szolgaltatas] })),
    ...NEV_KEZDETEK.map((n) => ({ namePrefix: n })),
  ];
}

/* Az utolsó nyomtató a böngésző tárolójában, hogy legközelebb ne kelljen keresni. */
const EMLEK_KULCS = 'mhb.nyomtato.eszkoz';

export interface NyomtatoEmlek {
  id: string | null;
  nev: string | null;
}

export function nyEmlek(): NyomtatoEmlek | null {
  try {
    const s = localStorage.getItem(EMLEK_KULCS);
    if (!s) return null;
    const e = JSON.parse(s) as NyomtatoEmlek;
    return e && (e.id || e.nev) ? e : null;
  } catch {
    /* privát böngészés vagy tiltott tároló — ilyenkor nincs emlék, ennyi */
    return null;
  }
}

function emlekMentes(eszkoz: BluetoothEszkoz): void {
  try {
    localStorage.setItem(
      EMLEK_KULCS,
      JSON.stringify({ id: eszkoz.id ?? null, nev: eszkoz.name ?? null }),
    );
  } catch {
    /* nem sikerült megjegyezni — a párosítás ettől még működik */
  }
}

/** Írható-e: nyers parancsot csak ilyen jellemzőre lehet küldeni. */
function irhato(j: BluetoothJellemzo): boolean {
  return !!j.properties?.write || !!j.properties?.writeWithoutResponse;
}

/**
 * Megkeressük, melyik szolgáltatáson lehet a nyomtatónak írni.
 *
 * Előbb a listánkat próbáljuk, mert az gyors és biztos. Ha egyik sem
 * passzol, végigjárjuk, amit az eszköz kiad, és az első írható jellemzőt
 * használjuk — így olyan nyomtató is működik, amit még nem láttunk.
 */
async function iroJellemzo(
  kapcsolat: BluetoothKapcsolat,
): Promise<{ jellemzo: BluetoothJellemzo; honnan: string }> {
  /*
   * A nyugtázás nélkül írható jellemző az elsődleges: a nyomtatók szinte
   * mindig azon fogadják az adatot. Ha egy szolgáltatásban több írható is
   * van, ezért rangsorolunk.
   */
  const legjobb = (lista: BluetoothJellemzo[]): BluetoothJellemzo | null =>
    lista.filter(irhato).sort(
      (a, b) => Number(!!b.properties?.writeWithoutResponse) - Number(!!a.properties?.writeWithoutResponse),
    )[0] ?? null;

  for (const s of SZOLGALTATASOK) {
    try {
      const szolg = await kapcsolat.getPrimaryService(s.szolgaltatas);
      if (s.jellemzo) {
        try {
          const j = await szolg.getCharacteristic(s.jellemzo);
          if (irhato(j)) return { jellemzo: j, honnan: `${s.nev} · ${j.uuid}` };
        } catch {
          /* megvan a szolgáltatás, de más a jellemzője — lejjebb végignézzük */
        }
      }
      const j = legjobb(await szolg.getCharacteristics());
      if (j) return { jellemzo: j, honnan: `${s.nev} · ${j.uuid}` };
    } catch {
      /* ezt a szolgáltatást nem adja ki — jöhet a következő */
    }
  }

  /* Amit nem ismerünk: végigjárjuk, amit az eszköz kiad magáról. */
  const talalt: string[] = [];
  for (const szolg of await kapcsolat.getPrimaryServices().catch(() => [])) {
    talalt.push(String(szolg.uuid));
    const j = legjobb(await szolg.getCharacteristics().catch(() => []));
    if (j) return { jellemzo: j, honnan: `${szolg.uuid} · ${j.uuid}` };
  }

  throw new NyomtatoHiba(
    'Ehhez a nyomtatóhoz nem találtunk nyomtatási csatornát. ' +
      (talalt.length
        ? `Amit kiadott magáról: ${talalt.join(', ')}. Küldd át ezt a sort, és felvesszük.`
        : 'Lehet, hogy párosítani kell előbb a telefon Bluetooth-beállításaiban.'),
  );
}

/** Kapcsolódás egy kiválasztott eszközhöz, és a nyomtatási csatorna megkeresése. */
async function kapcsol(eszkoz: BluetoothEszkoz): Promise<string> {
  const kapcsolat = await eszkoz.gatt?.connect();
  if (!kapcsolat) {
    throw new NyomtatoHiba(`A(z) ${eszkoz.name || 'nyomtató'} nem válaszol. Be van kapcsolva?`);
  }
  const { jellemzo, honnan } = await iroJellemzo(kapcsolat);
  nyomtato = { nev: eszkoz.name || 'nyomtató', eszkoz, jellemzo, csatorna: honnan };
  emlekMentes(eszkoz);
  return nyomtato.nev;
}

/** Hogyan keressük az eszközt a párosító ablakban. */
export type ParositasMod = 'szurt' | 'mind' | 'emlekezett';

/**
 * Párosítás: a böngésző mutatja a szóba jövő eszközöket, a sofőr választ.
 *
 * Három út van, és ez a sorrend is:
 * - `emlekezett` — ha már nyomtattunk ezen a gépen, **csak azt az egy
 *   nyomtatót** kínáljuk fel név szerint. Egytételes lista, nincs mit
 *   keresni benne.
 * - `szurt` — a nyomtatónak látszó eszközök (név és szolgáltatás szerint).
 * - `mind` — minden közeli eszköz. Ez a vészkijárat, ha a nyomtató a szűrt
 *   listából kimaradna; eddig ez volt az egyetlen út, ezért volt olyan hosszú
 *   a lista. (2026-11-04)
 */
export async function nyParosit(mod: ParositasMod = 'szurt', nev?: string | null): Promise<string> {
  if (!nyTamogatott()) throw new NyomtatoHiba(NEM_TUD);
  const keres: BluetoothKeres = {
    /* Amit nem sorolunk fel, ahhoz a böngésző nem enged hozzáférni. */
    optionalServices: SZOLGALTATASOK.map((s) => s.szolgaltatas),
  };
  const nevre = (nev ?? nyEmlek()?.nev ?? '').trim();
  if (mod === 'mind') keres.acceptAllDevices = true;
  else if (mod === 'emlekezett' && nevre) keres.filters = [{ name: nevre }];
  else keres.filters = nyomtatoSzuro();

  let eszkoz: BluetoothEszkoz;
  try {
    eszkoz = await navigator.bluetooth!.requestDevice(keres);
  } catch (h) {
    const uzenet = h instanceof Error ? h.message : String(h);
    /* A böngésző ugyanazt a hibát adja arra is, ha nincs találat, és arra is,
       ha a felhasználó bezárta az ablakot. Ezért szól kétfelé az üzenet. */
    if (!/cancel|notfound|no devices|user/i.test(uzenet)) throw h;
    if (mod === 'emlekezett' && nevre) {
      throw new NyomtatoHiba(
        `A(z) ${nevre} most nem látszik. Be van kapcsolva, és a közelben van? ` +
          'Másik nyomtatóhoz a „Másik nyomtató" gomb kell.',
      );
    }
    if (mod === 'szurt') {
      throw new NyomtatoHiba(
        'Nem választottál nyomtatót. Ha a nyomtató nem volt a listában, ' +
          'nyomd meg a „Minden eszköz" gombot — ott minden közeli eszköz megjelenik.',
      );
    }
    throw new NyomtatoHiba('Nem választottál eszközt.');
  }
  return await kapcsol(eszkoz);
}

/**
 * Csendes visszakapcsolás párosító ablak nélkül. (2026-11-04)
 *
 * A böngésző megjegyzi, melyik eszközhöz adtunk engedélyt, így a lap
 * újratöltése után a nyomtatóhoz **kérdés nélkül** vissza tudunk kapcsolni.
 * (Ehhez a böngészőnek támogatnia kell az engedélyezett eszközök
 * felsorolását; ha nem tudja, egyszerűen nem történik semmi, és marad a
 * párosító gomb.)
 */
export async function nyVisszaKapcsol(): Promise<string | null> {
  if (nyomtato?.eszkoz.gatt?.connected) return nyomtato.nev;
  const bt = typeof navigator === 'undefined' ? undefined : navigator.bluetooth;
  if (!bt?.getDevices) return null;
  const be = nyEmlek();
  try {
    const lista = await bt.getDevices();
    const e =
      lista.find((x) => !!be?.id && x.id === be.id) ??
      lista.find((x) => !!be?.nev && x.name === be.nev) ??
      (lista.length === 1 ? lista[0] : undefined);
    if (!e) return null;
    return await kapcsol(e);
  } catch {
    /* nincs a közelben vagy ki van kapcsolva — nem baj, marad a gomb */
    return null;
  }
}

/** A párosítás elfelejtése — ha másik nyomtatóra váltunk. (2026-11-04) */
export function nyLevalaszt(): void {
  try {
    nyomtato?.eszkoz.gatt?.disconnect?.();
  } catch {
    /* már le volt választva */
  }
  nyomtato = null;
  try {
    localStorage.removeItem(EMLEK_KULCS);
  } catch {
    /* nincs tároló — nincs mit törölni */
  }
}

/** Melyik csatornán megy a nyomtatás — hibakereséshez. */
export function parositottCsatorna(): string | null {
  return nyomtato?.csatorna ?? null;
}

/**
 * Nyomtatás előtt: legyen élő kapcsolat, de **ne** nyíljon párosító ablak,
 * ha nem muszáj. (2026-11-04)
 *
 * Sorrend: ami már él → csendes visszakapcsolás → az emlékezett nyomtató
 * név szerint → szűrt lista. Eddig minden nyomtatásnál a „minden eszköz"
 * lista jött fel, ha a lap közben újratöltődött.
 */
async function nyKeszenAll(): Promise<void> {
  if (nyomtato?.eszkoz.gatt?.connected) return;
  if (nyomtato) {
    /* Elaludt vagy kikapcsolt közben: ugyanahhoz az eszközhöz kapcsolunk vissza. */
    try {
      await kapcsol(nyomtato.eszkoz);
      return;
    } catch {
      nyomtato = null;
    }
  }
  if (await nyVisszaKapcsol()) return;
  await nyParosit(nyEmlek()?.nev ? 'emlekezett' : 'szurt');
}

/**
 * Küldés darabokban. (2026-10-07)
 *
 * **20 bájtonként.** A Bluetooth alapértelmezett csomagmérete ennyit enged
 * át, és a kézi nyomtatók jellemzően nem állítanak nagyobbat. Korábban
 * 100 bájtos darabokat küldtünk: a nyomtató fogadta a kapcsolatot, a címke
 * „elment", de nem történt semmi, mert a csomagok elvesztek. Egy címke így
 * is csak néhány írás.
 *
 * Nyugtázás nélkül írunk, ha a nyomtató azt tudja — a legtöbb így várja.
 */
async function kuld(adat: Uint8Array): Promise<void> {
  if (!nyomtato) throw new NyomtatoHiba('Nincs párosított nyomtató.');
  const j = nyomtato.jellemzo;
  const nyugtazasNelkul = !!j.properties?.writeWithoutResponse && !!j.writeValueWithoutResponse;
  const meret = 20;
  for (let i = 0; i < adat.length; i += meret) {
    const darab = adat.slice(i, i + meret);
    if (nyugtazasNelkul) await j.writeValueWithoutResponse!(darab);
    else await j.writeValue(darab);
    await new Promise((kesz) => setTimeout(kesz, 20));
  }
}

/**
 * Címkék nyomtatása: hordónként annyi példány, amennyit a sofőr beállított.
 * Ha még nincs párosítva a nyomtató, előbb azt kéri.
 */
export async function cimketNyomtat(
  szam: string,
  hordo: number,
  be: NyomtatoBeallitas,
  ceg?: CegAdat,
): Promise<number> {
  await nyKeszenAll();
  const darab = Math.max(1, Math.round(hordo));
  const peldany = Math.max(1, Math.round(be.pld));
  const adat = cimkeAdat(cimkeTartalom(szam, ceg), be);
  for (let i = 0; i < darab * peldany; i++) await kuld(adat);
  return darab * peldany;
}

/**
 * Raklapcímke nyomtatása: annyi példány, amennyit kérnek.
 * (A megrendelő kérése, 2026-10-16.)
 */
export async function raklapotNyomtat(
  tipus: 'HSO' | 'MO',
  darab: number,
  be: NyomtatoBeallitas,
): Promise<number> {
  await nyKeszenAll();
  const mennyi = Math.max(1, Math.round(darab));
  const adat = cimkeAdat(raklapTartalom(tipus), be);
  for (let i = 0; i < mennyi; i++) await kuld(adat);
  return mennyi;
}

/**
 * Próbacímke a beállítások hangolásához. (2026-10-01)
 *
 * Ugyanazt nyomtatja, mint az éles címke, csak kitalált sorszámmal — így a
 * sorköz és a papírtolás kipróbálható anélkül, hogy okmányt kellene
 * kiállítani hozzá.
 */
export async function probaNyomtatas(be: NyomtatoBeallitas, ceg?: CegAdat): Promise<void> {
  await nyKeszenAll();
  await kuld(cimkeAdat(cimkeTartalom('PROBA/000/000000/01', ceg), be));
}

/* ----------------------------- előnézet ------------------------------- */

/** A címkék papíron: nyomtató nélkül is megnézhető, és ki is nyomtatható. */
export const CIMKE_CSS = `
  .cimkelap { display: flex; flex-wrap: wrap; gap: 10px; }
  .cimke { background: #fff; border: 1px dashed #999; padding: 8px 6px; text-align: center;
           font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 10px; line-height: 1.35; }
  .cimke .cnagy { font-size: 12px; font-weight: 700; letter-spacing: .03em; padding: 2px 0 3px;
                  white-space: nowrap; }
  /*
   * A típus külön sorban, jóval nagyobban — ahogy a nyomtatóra is megy.
   * (2026-10-07)
   */
  .cimke .ctipus { font-size: 34px; font-weight: 700; line-height: 1.1; letter-spacing: .04em; }
  .cimke .capro { font-size: 11px; line-height: 1.35; }
  .cimke .csor { font-size: 11px; margin-top: 4px; }
`;

/** A címke sorai HTML-ben — az előnézet ugyanazt mutatja, ami nyomtatódik. */
function elonezetSorok(sorok: CimkeSor[]): string {
  return sorok
    .map((s) => {
      if (s.tipus === 'nagy' && s.kiemelt && s.sz.startsWith(s.kiemelt)) {
        /* Két sor: fölül a típus, alatta a sorszám többi része. */
        const maradek = s.sz.slice(s.kiemelt.length);
        return (
          `<div class="ctipus">${esc(s.kiemelt)}</div>` +
          (maradek ? `<div class="cnagy">${esc(maradek)}</div>` : '')
        );
      }
      if (s.tipus === 'nagy') return `<div class="cnagy">${esc(s.sz)}</div>`;
      if (s.tipus === 'apro') return `<div class="capro">${esc(s.sz)}</div>`;
      return `<div class="csor">${esc(s.sz)}</div>`;
    })
    .join('');
}

/** Raklapcímke előnézete — nyomtató nélkül is megnézhető. (2026-10-16) */
export function raklapElonezet(tipus: 'HSO' | 'MO', darab: number, szeles: number): void {
  const mennyi = Math.max(1, Math.round(darab));
  const szelesseg = szeles === 80 ? '76mm' : '52mm';
  const sorok = elonezetSorok(raklapTartalom(tipus));
  const cimkek = Array.from(
    { length: mennyi },
    () => `<div class="cimke" style="width:${szelesseg}">${sorok}</div>`,
  ).join('');
  dokumentumMegjelenites(
    ablakotNyit(CIMKE_CSS),
    `Raklapcímke — ${tipus} (${mennyi} db)`,
    `<div class="lap"><div class="cimkelap">${cimkek}</div></div>`,
    CIMKE_CSS,
  );
}

export function cimkeElonezet(szam: string, hordo: number, szeles: number, ceg?: CegAdat): void {
  const darab = Math.max(1, Math.round(hordo));
  const szelesseg = szeles === 80 ? '76mm' : '52mm';
  /* Az előnézet ugyanazt mutatja, ami a nyomtatóra megy. */
  const sorok = cimkeTartalom(szam, ceg)
    .map((s) => {
      if (s.tipus === 'nagy' && s.kiemelt && s.sz.startsWith(s.kiemelt)) {
        /* Két sor: fölül a típus, alatta a sorszám többi része. */
        return (
          `<div class="ctipus">${esc(s.kiemelt)}</div>` +
          `<div class="cnagy">${esc(s.sz.slice(s.kiemelt.length))}</div>`
        );
      }
      if (s.tipus === 'nagy') return `<div class="cnagy">${esc(s.sz)}</div>`;
      if (s.tipus === 'apro') return `<div class="capro">${esc(s.sz)}</div>`;
      return `<div class="csor">${esc(s.sz)}</div>`;
    })
    .join('');
  const cimkek = Array.from(
    { length: darab },
    () => `<div class="cimke" style="width:${szelesseg}">${sorok}</div>`,
  ).join('');
  dokumentumMegjelenites(
    ablakotNyit(CIMKE_CSS),
    `Hordócímke — ${szam} (${darab} db)`,
    `<div class="lap"><div class="cimkelap">${cimkek}</div></div>`,
    CIMKE_CSS,
  );
}
